import { IS_DE_FR } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { isInZeloolApp } from '@/utils/tools';
import logger from '@/utils/client-logger';
import { ApplovinProvider } from './providers/applovinProvider';
import { AwinProvider } from './providers/awinProvider';
import { AwsWafProvider } from './providers/awsWafProvider';
import { BingProvider } from './providers/bingProvider';
import { ConsentManagerProvider } from './providers/consentManagerProvider';
import { CriteoProvider } from './providers/criteoProvider';
import { EmarsysProvider } from './providers/emarsysProvider';
import { FacebookProvider } from './providers/facebookProvider';
import { Ga4Provider } from './providers/ga4Provider';
import { GoogleBadgeProvider } from './providers/googleBadgeProvider';
import { GoogleTranslateProvider } from './providers/googleTranslateProvider';
import { ImpactRadiusProvider } from './providers/impactProvider';
import { OneTrustProvider } from './providers/oneTrustProvider';
import { OpenAiPixelProvider } from './providers/openAiPixelProvider';
import { PepperjamProvider } from './providers/pepperjamProvider';
import { RtbHouseProvider } from './providers/rtbHouseProvider';
import { SensorsProvider } from './providers/sensorProvider';
import { SnapchatProvider } from './providers/snapchatProvider';
import { SnapchatConversionsProvider } from './providers/snapchatConversionsProvider';
import { TiktokProvider } from './providers/tiktokProvider';
import { TwitterProvider } from './providers/twitterProvider';
import { SnowplowProvider } from './providers/snowplowProvider';
import { ReportingCenter } from './reportingCenter';
import { resolveSnowplowAppId, resolveSnowplowGzipEnabled } from './snowplowConfig';
import {
  SgtmPixelConfig,
  TrackingConfig,
  getTrackingEnabled,
  parseSgtmPixelConfig,
  resolveProviderEnabled,
} from './trackingConfig';
import { generateEventId } from './utils/eventIdGenerator';
import { initTrackingDebug } from './trackingDebug';
import { ProviderType } from './types';

// Export the types and main class
export * from './types';
export { ReportingCenter };
export type { TrackingConfig, SgtmPixelConfig };

// Initialize reporting on app startup
export async function initializeReporting(
  deviceType: EPlatform,
  trackingConfig?: TrackingConfig,
  sgtmPixelConfig?: SgtmPixelConfig | null,
): Promise<ReportingCenter> {
  const center = ReportingCenter.getInstance();
  const isInZeloolAppFlag = isInZeloolApp();

  // 将 sgtm_pixel_config 转为统一格式，方便 getTrackingEnabled 复用
  const sgtmAsTracking = parseSgtmPixelConfig(sgtmPixelConfig);

  logger.info('[initializeReporting] sgtmPixelConfig =', sgtmPixelConfig ?? 'null');

  // 辅助函数：合并多层配置源，确定最终 isEnabled
  const resolve = (
    providerType: ProviderType,
    envEnabled: boolean,
    runtimeConstraint?: boolean,
  ) => {
    const sgtmEnabled = getTrackingEnabled(sgtmAsTracking, providerType);
    const trackingEnabled = getTrackingEnabled(trackingConfig, providerType);
    const result = resolveProviderEnabled({
      runtimeConstraint,
      sgtmPixelConfigEnabled: sgtmEnabled,
      trackingConfigEnabled: trackingEnabled,
      envEnabled,
    });

    if (sgtmEnabled !== undefined) {
      const overrideInfo = envEnabled !== result ? `(overrides env=${envEnabled})` : '';
      logger.info(
        `[initializeReporting] ${providerType}: sgtm=${sgtmEnabled} → ${result ? 'enabled' : 'disabled'} ${overrideInfo}`,
      );
    }

    return result;
  };

  // Sensors Provider — 原来硬编码 true，现在可被 BFF 配置覆盖
  center.registerProvider(
    new SensorsProvider({
      serverUrl: env.NEXT_PUBLIC_SENSORS_SERVER_URL,
      showLog: env.NEXT_PUBLIC_SENSORS_SHOW_LOG,
      platform: deviceType === EPlatform.WEB ? 'Web' : 'Wap',
      platformName:
        deviceType === EPlatform.WEB
          ? (env.NEXT_PUBLIC_SENSORS_PUB_PLATFORM_NAME ?? 'zeeloolWeb')
          : (env.NEXT_PUBLIC_SENSORS_PUB_WAP_PLATFORM_NAME ?? 'zeeloolWap'),
      envName: env.NEXT_PUBLIC_ENV === 'local' ? 'dev' : env.NEXT_PUBLIC_ENV,
      isEnabled: resolve(ProviderType.SENSORS, true),
    }),
  );

  // OneTrust Provider，顺序放在前面，尽量在GA4之前就加载好
  center.registerProvider(
    new OneTrustProvider({
      domainScript: 'ad7e7fb7-fa0f-4d1c-8f54-d32280690460',
      isEnabled: resolve(
        ProviderType.ONETRUST,
        env.NEXT_PUBLIC_ENABLE_ONETRUST === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  center.registerProvider(
    new ConsentManagerProvider({
      cdid: env.NEXT_PUBLIC_CONSENT_MANAGER_CDID,
      isEnabled: resolve(
        ProviderType.CONSENT_MANAGER,
        env.NEXT_PUBLIC_ENABLE_CONSENT_MANAGER === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // Emarsys Provider
  center.registerProvider(
    new EmarsysProvider({
      isEnabled: resolve(
        ProviderType.EMARSYS,
        env.NEXT_PUBLIC_ENABLE_EMARSYS === 'true',
        !isInZeloolAppFlag,
      ),
      domainCode: env.NEXT_PUBLIC_EMARSYS_DOMAIN_CODE,
      defaultNotificationTitle: env.NEXT_PUBLIC_EMARSYS_DEFAULT_NOTIFICATION_TITLE,
      defaultNotificationIcon: env.NEXT_PUBLIC_EMARSYS_DEFAULT_NOTIFICATION_ICON,
      applicationServerPublicKey: env.NEXT_PUBLIC_EMARSYS_APPLICATION_SERVER_PUBLIC_KEY,
      scarabJsApiUrl: env.NEXT_PUBLIC_EMARSYS_SCARAB_JS_API_URL,
    }),
  );

  // Impact Radius Provider
  center.registerProvider(
    new ImpactRadiusProvider({
      isEnabled: resolve(
        ProviderType.IMPACT_RADIUS,
        env.NEXT_PUBLIC_ENABLE_IMPACT_RADIUS === 'true',
      ),
    }),
  );

  // Awin Provider
  center.registerProvider(
    new AwinProvider({
      merchantId: env.NEXT_PUBLIC_AWIN_MERCHANT,
      isEnabled: resolve(ProviderType.AWIN, env.NEXT_PUBLIC_ENABLE_AWIN === 'true'),
    }),
  );

  // AppLovin Provider
  center.registerProvider(
    new ApplovinProvider({
      eventKey: env.NEXT_PUBLIC_APPLOVIN_KEY,
      isEnabled: resolve(ProviderType.APPLOVIN, env.NEXT_PUBLIC_ENABLE_APPLOVIN === 'true'),
    }),
  );

  // Bing Provider
  center.registerProvider(
    new BingProvider({
      trackingId: env.NEXT_PUBLIC_BING_ID,
      isEnabled: resolve(
        ProviderType.BING,
        env.NEXT_PUBLIC_ENABLE_BING === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // Criteo Provider
  center.registerProvider(
    new CriteoProvider({
      account: Number(env.NEXT_PUBLIC_CRITEO_ID),
      isEnabled: resolve(ProviderType.CRITEO, env.NEXT_PUBLIC_ENABLE_CRITEO === 'true'),
    }),
  );

  // Facebook Provider
  center.registerProvider(
    new FacebookProvider({
      pixelId: env.NEXT_PUBLIC_FACEBOOK_ID,
      isEnabled: resolve(
        ProviderType.FACEBOOK,
        env.NEXT_PUBLIC_ENABLE_FACEBOOK === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // GA4 Provider
  center.registerProvider(
    new Ga4Provider({
      measurementId: env.NEXT_PUBLIC_GA4_MEASUREMENT_ID,
      adId1: env.NEXT_PUBLIC_GA4_AD_ID1,
      adId2: env.NEXT_PUBLIC_GA4_AD_ID2,
      isEnabled: resolve(ProviderType.GA4, env.NEXT_PUBLIC_ENABLE_GA4 === 'true'),
    }),
  );

  // Pepperjam Provider
  center.registerProvider(
    new PepperjamProvider({
      containerId: env.NEXT_PUBLIC_PEPPERJAM_ID,
      isEnabled: resolve(ProviderType.PEPPERJAM, env.NEXT_PUBLIC_ENABLE_PEPPERJAM === 'true'),
    }),
  );

  // Snapchat Provider
  // 生成共享 PAGE_VIEW eventId，Pixel 侧与 CAPI 侧使用同一值实现去重
  const snapchatPageViewEventId = generateEventId();
  center.registerProvider(
    new SnapchatProvider({
      appKey: env.NEXT_PUBLIC_SNAPCHAT_KEY,
      isEnabled: resolve(ProviderType.SNAPCHAT, env.NEXT_PUBLIC_ENABLE_SNAPCHAT === 'true'),
      pageViewEventId: snapchatPageViewEventId,
    }),
  );

  /**
   * 作为 SnapchatProvider 补充信号
   * Snapchat Conversions Provider
   * 任务诉求: https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/request/49e830ab4fb965ccb3c977a55b
   * 详细参数文档: https://developers.snap.com/api/marketing-api/Conversions-API/Parameters
   */
  center.registerProvider(
    new SnapchatConversionsProvider({
      isEnabled: resolve(
        ProviderType.SNAPCHAT_CONVERSION,
        env.NEXT_PUBLIC_ENABLE_SNAPCHAT_CONVERSION === 'true',
      ),
      platform: deviceType === EPlatform.WEB ? 'WEB' : 'MOBILE_APP',
      pageViewEventId: snapchatPageViewEventId,
    }),
  );

  // TikTok Provider
  center.registerProvider(
    new TiktokProvider({
      pixelId: env.NEXT_PUBLIC_TIKTOK_PIXEL_KEY,
      isEnabled: resolve(
        ProviderType.TIKTOK,
        env.NEXT_PUBLIC_ENABLE_TT_PIXEL === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // OpenAI Ads Measurement Pixel
  center.registerProvider(
    new OpenAiPixelProvider({
      pixelId: env.NEXT_PUBLIC_OPENAI_PIXEL_ID ?? '',
      isEnabled: resolve(
        ProviderType.OPENAI,
        env.NEXT_PUBLIC_ENABLE_OPENAI_PIXEL === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // Twitter Provider
  center.registerProvider(
    new TwitterProvider({
      pixelIds: env.NEXT_PUBLIC_TWITTER_KEYS || [],
      isEnabled: resolve(ProviderType.TWITTER, env.NEXT_PUBLIC_ENABLE_TWITTER === 'true'),
    }),
  );

  // RTB House Provider
  center.registerProvider(
    new RtbHouseProvider({
      trackingId: env.NEXT_PUBLIC_RTB_HOUSE_ID,
      isEnabled: resolve(ProviderType.RTB_HOUSE, env.NEXT_PUBLIC_ENABLE_RTB_HOUSE === 'true'),
      dataCenter: env.NEXT_PUBLIC_RTB_HOUSE_DATA_CENTER,
    }),
  );

  // Google Translate Provider
  center.registerProvider(
    new GoogleTranslateProvider({
      isEnabled: resolve(
        ProviderType.GOOGLE_TRANSLATE,
        env.NEXT_PUBLIC_ENABLE_GOOGLE_TRANSLATE === 'true',
        !isInZeloolAppFlag,
      ),
    }),
  );

  // AWS WAF Provider
  center.registerProvider(
    new AwsWafProvider({
      challengeSdkUrl: env.NEXT_PUBLIC_AWS_WAF_CHALLENGE_SDK_URL || '',
      captchaSdkUrl: env.NEXT_PUBLIC_AWS_WAF_CAPTCHA_SDK_URL || '',
      isEnabled: resolve(ProviderType.AWS_WAF, env.NEXT_PUBLIC_ENABLE_AWS_WAF === 'true'),
    }),
  );

  // Google Badge Provider - 只在WEB端显示
  // todo 现在只包含老的谷歌徽章，德法要使用新的谷歌徽章
  center.registerProvider(
    new GoogleBadgeProvider({
      merchantId: env.NEXT_PUBLIC_GOOGLE_BADGE_ID,
      isEnabled: resolve(ProviderType.GOOGLE_BADGE, deviceType === EPlatform.WEB && !IS_DE_FR),
    }),
  );

  // Snowplow Provider — 自研埋点上报，与神策 1:1 对应
  // App 内通过 webview-tracker 上报（不依赖 runtimeConstraint），浏览器侧通过 browser-tracker 上报
  center.registerProvider(
    new SnowplowProvider({
      collectorUrl:
        env.NEXT_PUBLIC_SNOWPLOW_COLLECTOR_URL ?? 'https://www.zeelool.com/zlmkt-metrics/',
      appId: resolveSnowplowAppId(
        env.NEXT_PUBLIC_SNOWPLOW_APP_ID,
        deviceType,
        env.NEXT_PUBLIC_SNOWPLOW_ENV,
      ),
      platform: deviceType === EPlatform.WEB ? 'Web' : 'Wap',
      platformName:
        deviceType === EPlatform.WEB
          ? (env.NEXT_PUBLIC_SENSORS_PUB_PLATFORM_NAME ?? 'zeeloolWeb')
          : (env.NEXT_PUBLIC_SENSORS_PUB_WAP_PLATFORM_NAME ?? 'zeeloolWap'),
      envName: env.NEXT_PUBLIC_ENV === 'local' ? 'dev' : env.NEXT_PUBLIC_ENV,
      isEnabled: resolve(ProviderType.SNOWPLOW, env.NEXT_PUBLIC_ENABLE_SNOWPLOW === 'true'),
      gzipEnabled: resolveSnowplowGzipEnabled(env.NEXT_PUBLIC_ENABLE_SNOWPLOW_GZIP),
      errorTrackingEnabled: env.NEXT_PUBLIC_ENABLE_SNOWPLOW_ERROR_TRACKING === 'true',
    }),
  );

  // 开发/测试环境初始化调试工具
  if (['local', 'dev', 'test', 'test2'].includes(env.NEXT_PUBLIC_ENV)) {
    initTrackingDebug(trackingConfig, sgtmPixelConfig);
  }

  // Initialize all providers (only enabled ones will actually initialize)
  await center.initialize();

  return center;
}
